import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, Users, ArrowUpRight, Shield, Clock, Zap, Palette, Code, Phone, Flame, Layers, UserPlus, Share2, Sparkles, Settings, Star, Mic, Globe } from 'lucide-react';
import SEO from '@/components/SEO';
import PaymentMethods from '@/components/PaymentMethods';
import BrandMarquee from '@/components/BrandMarquee';
import { site, pricingPlans, faqs, comparison, calculator, customerJourney, pricingAddons } from '@/data/content';
import { useLocation } from '@/contexts/LocationContext';

function PricingFaq({ question, answer, isOpen, onClick }: { question: string; answer: string; isOpen: boolean; onClick: () => void }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 active:scale-[0.99] ${
        isOpen ? 'shadow-md' : ''
      }`}
    >
      <button onClick={onClick} className="flex w-full items-center justify-between text-left" aria-expanded={isOpen}>
        <span className="pr-4 font-semibold text-slate-900">{question}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] pt-3' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <p className="text-slate-600">{answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [open, setOpen] = useState<number | null>(null);
  const [agents, setAgents] = useState(5);
  const { location, setCurrency, convertPrice, formatPrice, getCurrencySymbol, availableCurrencies, isLoading } = useLocation();

  const maxPrice = Math.max(...calculator.competitors.map((c) => c.price));
  const zuriCost = convertPrice(calculator.zuriPrice * agents * 12);
  const topCompetitorCost = convertPrice(maxPrice * agents * 12);
  const annualSavings = topCompetitorCost - zuriCost;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: site.name,
      description: site.description,
      offers: pricingPlans.map((plan) => ({
        '@type': 'Offer',
        name: plan.name,
        description: plan.description,
        url: `${site.baseUrl}/pricing`,
        price: plan.priceUSD === 0 ? '0' : convertPrice(plan.priceUSD).toString(),
        priceCurrency: location.currency,
        availability: 'https://schema.org/InStock',
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  return (
    <>
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for ZuriHub. Free, Pro, and Enterprise plans."
        path="/pricing"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-900/20 px-4 py-1.5 text-sm font-medium text-brand-300">
            <Zap className="h-4 w-4" /> No hidden fees. No annual lock-in.
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Pay less. Support <span className="gradient-text">better</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300">
            One platform for chat, tickets, knowledge base, AI and voice. Start free, then scale at $25 per agent per month. Enterprise at $390 for 10 agents with advanced AI and 24/7 support.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/book-a-demo"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-semibold text-white transition hover:bg-brand-700"
            >
              Start Free - No Credit Card <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="#addons"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Explore add-ons
            </a>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-10" aria-labelledby="trust-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { icon: 'Zap', title: 'Start in minutes', text: 'Free trial. No credit card.' },
              { icon: 'Clock', title: 'Cancel anytime', text: 'No annual lock-in. No hidden fees.' },
              { icon: 'Shield', title: '99.99% uptime SLA', text: 'Enterprise-ready reliability.' },
            ].map((t) => (
              <div key={t.title} className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-5">
                {t.icon === 'Zap' ? <Zap className="h-6 w-6 text-brand-600" /> : t.icon === 'Clock' ? <Clock className="h-6 w-6 text-brand-600" /> : <Shield className="h-6 w-6 text-brand-600" />}
                <div>
                  <p className="font-bold text-slate-900">{t.title}</p>
                  <p className="text-sm text-slate-600">{t.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="pricing-plans">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="pricing-plans" className="section-title">Simple, transparent pricing</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Start free, scale at {formatPrice(25)} per agent. Add enterprise AI features when you're ready.
            </p>
          </div>
          <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
            <div className="flex items-center justify-between mb-3">
              <p>
                <span className="font-semibold text-slate-900">All prices include VAT and applicable taxes.</span> Prices are quoted in {location.currency} and billed by Flutterwave.
              </p>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-slate-500" />
                <select
                  value={location.currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="text-sm font-semibold text-slate-900 bg-transparent border-none outline-none cursor-pointer"
                >
                  {availableCurrencies.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
              </div>
            </div>
            <p>You can pay by card, Google Pay, Apple Pay, Airtel/MTN MoMo, or bank transfer.</p>
            <PaymentMethods className="mt-4" />
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-3xl border p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${
                  plan.featured
                    ? 'border-brand-300 bg-gradient-to-br from-brand-50 to-white ring-2 ring-brand-200 shadow-lg'
                    : 'border-slate-200 bg-white'
                }`}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-brand-600 to-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-md">
                    <Star className="h-3 w-3 fill-white" />
                    Most Popular
                  </span>
                )}
                <h3 className={`text-2xl font-bold ${plan.featured ? 'text-brand-900' : 'text-slate-900'}`}>{plan.name}</h3>
                <p className="mt-2 text-slate-600">{plan.description}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className={`text-4xl font-extrabold ${plan.featured ? 'text-brand-700' : 'text-slate-900'}`}>
                    {plan.priceUSD === 0 ? 'Free' : formatPrice(convertPrice(plan.priceUSD))}
                  </span>
                  <span className="text-sm text-slate-500">/{plan.period}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${plan.featured ? 'text-brand-600' : 'text-green-600'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                {plan.href.startsWith('http') ? (
                  <a
                    href={plan.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition ${
                      plan.featured
                        ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white hover:from-brand-700 hover:to-indigo-700'
                        : 'border border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-600'
                    }`}
                  >
                    {plan.cta} <ArrowRight className="h-4 w-4" />
                  </a>
                ) : (
                  <Link
                    to={plan.href}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition ${
                      plan.featured
                        ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white hover:from-brand-700 hover:to-indigo-700'
                        : 'border border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-600'
                    }`}
                  >
                    {plan.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="journey-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="journey-heading" className="section-title">Your customer journey with ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              From first agent to Enterprise. Upgrade only when your business is ready.
            </p>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 bg-slate-200" />
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {customerJourney.map((step, i) => {
                const StepIcon = [UserPlus, Share2, Sparkles, Settings][i];
                return (
                  <div key={step.step} className="relative rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                      <StepIcon className="h-5 w-5" />
                    </div>
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-slate-900 px-3 py-0.5 text-xs font-bold text-white">{step.step}</span>
                    <h3 className="mt-5 font-bold text-slate-900">{step.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="addons" className="bg-slate-50 py-24" aria-labelledby="addons-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 id="addons-heading" className="section-title">Power up your workspace</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Enterprise AI features, premium themes, and advanced analytics. Add what you need, when you need it.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pricingAddons.map((addon) => {
              const AddonIcon =
                addon.icon === 'Palette' ? Palette :
                addon.icon === 'Code' ? Code :
                addon.icon === 'Phone' ? Phone :
                addon.icon === 'Flame' ? Flame :
                addon.icon === 'Users' ? Users :
                addon.icon === 'Mic' ? Mic :
                addon.icon === 'Shield' ? Shield :
                Layers;
              const isContact = addon.contactSales || addon.enterprise;
              const isFeatured = addon.name === 'Voice Number';
              const displayPrice = isContact 
                ? (addon.contactSales ? 'Contact sales' : 'Enterprise')
                : addon.priceUSD === 0 
                  ? 'Free'
                  : formatPrice(convertPrice(addon.priceUSD));
              return (
                <div
                  key={addon.name}
                  className={`flex flex-col rounded-3xl border p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg ${
                    isFeatured
                      ? 'border-brand-300 bg-gradient-to-br from-brand-50 to-white ring-2 ring-brand-200'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  {isFeatured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-brand-600 to-indigo-600 px-3 py-1 text-xs font-semibold text-white shadow-md">
                      <Star className="h-3 w-3 fill-white" />
                      Featured
                    </div>
                  )}
                  <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${
                    isFeatured ? 'bg-brand-600 text-white' : 'bg-brand-50 text-brand-700'
                  }`}>
                    <AddonIcon className="h-6 w-6" />
                  </div>
                  <h3 className={`text-xl font-bold ${isFeatured ? 'text-brand-900' : 'text-slate-900'}`}>{addon.name}</h3>
                  <p className="mt-2 flex-1 text-sm text-slate-600 leading-relaxed">{addon.description}</p>
                  <div className="mt-6">
                    <span className={`text-3xl font-extrabold ${isContact ? 'text-slate-700 text-2xl' : isFeatured ? 'text-brand-700' : 'text-brand-700'}`}>{displayPrice}</span>
                    {addon.period && <span className="text-sm text-slate-500">{addon.period}</span>}
                  </div>
                  <a
                    href={addon.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-4 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition ${
                      isFeatured
                        ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white hover:from-brand-700 hover:to-indigo-700'
                        : 'border border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-600'
                    }`}
                  >
                    {addon.cta} <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="savings-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="savings-heading" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                What you save vs the leading tools
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                ZuriHub replaces chat, tickets, knowledge base, and AI at one flat price. Compare for yourself.
              </p>
              <div className="mt-8 rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
                <p className="text-sm font-bold uppercase text-slate-400 tracking-wider">Annual savings vs {calculator.competitors[0].name}</p>
                <p className="mt-2 text-5xl font-extrabold">{formatPrice(annualSavings)}</p>
                <p className="mt-2 text-slate-400">for a team of {agents} agents at {formatPrice(convertPrice(25))}/agent</p>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
                >
                  Start Saving Today <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-xl sm:p-10">
              <div className="flex items-center justify-between">
                <label htmlFor="agents" className="text-sm font-bold text-slate-700">Number of agents</label>
                <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                  <Users className="h-4 w-4 text-slate-400" />
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={agents}
                    onChange={(e) => {
                      const v = parseInt(e.target.value);
                      setAgents(Math.max(1, Math.min(100, isNaN(v) ? 1 : v)));
                    }}
                    className="w-16 bg-transparent text-right font-bold text-slate-900 outline-none"
                  />
                </div>
              </div>
              <input
                id="agents"
                type="range"
                min={1}
                max={100}
                value={agents}
                onChange={(e) => setAgents(parseInt(e.target.value))}
                className="mt-4 w-full accent-brand-600"
              />
              <div className="mt-8 space-y-5">
                {calculator.competitors.map((comp) => {
                  const theirCost = convertPrice(comp.price * agents);
                  const save = theirCost - convertPrice(calculator.zuriPrice * agents);
                  return (
                    <div key={comp.name}>
                      <div className="flex items-center justify-between text-sm font-semibold">
                        <span className="text-slate-900">{comp.name}</span>
                        <span className="text-slate-500">{formatPrice(theirCost)}/mo</span>
                      </div>
                      <div className="mt-2 h-4 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-4 rounded-full transition-all duration-300 ${comp.color}`} style={{ width: `${(comp.price / maxPrice) * 100}%` }} />
                      </div>
                      <p className="mt-1 text-xs font-bold text-green-600">Save {formatPrice(save)}/mo vs {comp.name}</p>
                    </div>
                  );
                })}
                <div className="rounded-2xl bg-brand-50 p-5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">ZuriHub Pro</span>
                    <span className="text-2xl font-extrabold text-brand-700">{formatPrice(convertPrice(calculator.zuriPrice * agents))}/mo</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-600">
                    That is {formatPrice(convertPrice(maxPrice * agents))}/mo worth of CX software for {formatPrice(convertPrice(calculator.zuriPrice * agents))}.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 id="compare-heading" className="section-title">Why teams choose ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4">
              One platform, all channels, AI included, and a price that scales with your team.
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-6 py-4 font-semibold">Feature</th>
                  <th className="px-6 py-4 font-semibold text-brand-700">ZuriHub</th>
                  <th className="px-6 py-4 font-semibold">Intercom</th>
                  <th className="px-6 py-4 font-semibold">Zendesk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {comparison.rows.map((row) => (
                  <tr key={row.feature}>
                    <td className="px-6 py-4 font-semibold text-slate-900">{row.feature}</td>
                    <td className="px-6 py-4 font-bold text-brand-700">
                      {row.zurihub === true ? 'Yes' : row.zurihub === false ? 'No' : row.zurihub}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {row.intercom === true ? 'Yes' : row.intercom === false ? 'No' : row.intercom}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {row.zendesk === true ? 'Yes' : row.zendesk === false ? 'No' : row.zendesk}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="pricing-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="pricing-faq" className="section-title text-center">Frequently asked questions</h2>
          <p className="section-subtitle mx-auto mt-4">Straight answers about pricing, trials, and plans.</p>
          <div className="mt-10 space-y-4">
            {faqs.map((faq, i) => (
              <PricingFaq
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                isOpen={open === i}
                onClick={() => setOpen(open === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Still have questions?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Start a free 14-day Pro trial or talk to sales about Enterprise.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Start Free - No Credit Card <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
